import type { ApiClient } from '@carbonwebmail/api-client'
import type { SuppressionPageDto, SuppressionRowDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { useAction } from '../../ui/useAction.js'
import { usePaged } from '../../ui/usePaged.js'

/**
 * The suppression list, and the two ways it changes by hand.
 *
 * The filter is part of the loader rather than something applied to the rows
 * here, because the list is paged: filtering a page would show "3 of 50" from
 * whichever fifty happened to load, which is a different and wrong answer.
 *
 * Adding and removing both reload. The server decides what is on the list —
 * a suppression can arrive from a bounce webhook between two clicks — and
 * patching the rows here would show somebody a list the send path does not
 * agree with.
 */

export type SuppressionReasonFilter = SuppressionRowDto['reason'] | 'all'

/**
 * What removing each kind actually means, in the words the confirmation uses.
 *
 * Keyed by the contract's own union, so a reason added to it fails the build
 * here until somebody decides what removing that one means. The four are not
 * interchangeable: one is a person's stated choice, one is a dead mailbox, one
 * is somebody reporting you as spam, and one is a note a colleague left.
 */
const CONSEQUENCES: Record<SuppressionRowDto['reason'], string> = {
  unsubscribed:
    'asked to stop hearing from you. Removing this means you will mail them again, and in most places that is unlawful unless they have since asked to return.',
  hard_bounce:
    'has a mailbox that rejected mail permanently. Removing this means trying again — worth doing if it has been fixed, and damaging to your sending reputation if it has not.',
  complaint:
    'reported one of your messages as spam. Removing this means mailing somebody who has already told their provider you are a spammer, which is the fastest way to lose a sending domain.',
  manual:
    'was added by hand by somebody at your organisation. Removing this undoes whatever they knew — check the consent record for the note they left.',
}

export function confirmationFor(row: SuppressionRowDto): string {
  return `${row.email} ${CONSEQUENCES[row.reason]}\n\nRemove them from the list anyway?`
}

export function useSuppressions({ client }: { readonly client: ApiClient }) {
  const [reason, setReason] = useState<SuppressionReasonFilter>('all')

  const load = useCallback(
    (page: number) => client.suppressions(page, reason === 'all' ? undefined : reason),
    [client, reason],
  )
  const { state, setPage, retry } = usePaged<SuppressionPageDto>(
    load,
    'We could not load your suppression list. Please try again in a moment.',
  )

  const { notice, problem, busy, run } = useAction(retry)

  const suppress = useCallback(
    (email: string, note: string) =>
      run(async () => {
        await client.suppress({ emails: [email], note })
        return 'Added. No campaign will reach that address.'
      }, 'We could not add that address. Check it and try again.'),
    [client, run],
  )

  const unsuppress = useCallback(
    (email: string) =>
      run(async () => {
        const outcome = await client.unsuppress(email)
        return outcome.status === 'removed'
          ? 'Removed. Campaigns can reach that address again, and the consent record shows who did this.'
          : 'That address was not on the list.'
      }, 'We could not remove that address. Please try again in a moment.'),
    [client, run],
  )

  return {
    state,
    reason,
    setReason,
    setPage,
    notice,
    problem,
    busy,
    suppress,
    unsuppress,
    retry,
  }
}
